import * as XLSX from 'xlsx';
import { withDescriptions } from './descriptions';

/**
 * Parse Excel file and extract required columns
 * @param {File} file - Excel file
 * @returns {Promise<Array>} Parsed data
 */
export const parseExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                // Auto-detect the best sheet by checking for known input columns
                const knownColumns = [
                    'description', 'partnum', 'partnumber', 'part number', 'part no',
                    'manufacturernumber', 'manufacturer number', 'manf number',
                    'supplier partnumber', 'given_supplier partnumber',
                    'supplier partnumber 1', 'supplier partnumber 2',
                    'given_supplier name', 'supplier name',
                    'noun/modifier', 'noun modifier',
                    'item name', 'item description', 'description 1', 'description 2',
                ];
                let bestSheet = workbook.SheetNames[0];
                let bestScore = 0;

                for (const sheetName of workbook.SheetNames) {
                    const ws = workbook.Sheets[sheetName];
                    const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
                    if (rows.length === 0) continue;
                    const headers = Object.keys(rows[0]).map(h => h.toLowerCase().trim());
                    const score = headers.filter(h => knownColumns.some(kc => h.includes(kc) || kc.includes(h))).length;
                    if (score > bestScore) {
                        bestScore = score;
                        bestSheet = sheetName;
                    }
                }

                const worksheet = workbook.Sheets[bestSheet];

                // Convert to JSON
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

                // Extract required columns for both legacy and automation formats
                const parsedData = jsonData.map((row, index) => {
                    // Part Number — first column of the input file; identifies the record
                    // on the Results page and in export/import.
                    const partnum =
                        row['Part Number'] ||
                        row['PART NUMBER'] ||
                        row['Part number'] ||
                        row['part number'] ||
                        row['PartNumber'] ||
                        row['Part No'] ||
                        row['Part No.'] ||
                        row['Partnum'] ||
                        row['PartNum'] ||
                        row['partnum'] ||
                        row['PARTNUM'] ||
                        '';

                    // Try different possible column names for ManufacturerNumber
                    const manufacturerNumber =
                        row['ManufacturerNumber'] ||
                        row['Manufacturer Number'] ||
                        row['MANUFACTURER_NUMBER'] ||
                        row['Manf Number'] ||
                        row['given_Manufacturers part number1'] ||
                        row['given_Manufacturers Part Number1'] ||
                        '';

                    // Try different possible column names for Manufacturer Name
                    const manufacturerName =
                        row['Manufacturer Name'] ||
                        row['ManufacturerName'] ||
                        row['MANUFACTURER_NAME'] ||
                        row['Manufacturer'] ||
                        row['Brand'] ||
                        row['brand'] ||
                        row['given_Manufacture Name1'] ||
                        row['given_Manufacture name1'] ||
                        '';

                    const itemName =
                        row['Item Name'] ||
                        row['ItemName'] ||
                        row['ITEM_NAME'] ||
                        row['Item_Name'] ||
                        '';

                    const itemDescription1 =
                        row['Item Description 1'] || row['ITEM_DESCRIPTION 1'] ||
                        row['Item Description'] ||
                        row['ItemDescription'] ||
                        row['ITEM_DESCRIPTION'] ||
                        row['Item_Description'] ||
                        '';

                    const itemDescription2 =
                        row['Item Description 2'] || row['ITEM_DESCRIPTION 2'] ||
                        row['ItemDescription2'] || row['ITEM_DESCRIPTION_2'] || '';

                    const description1 =
                        row['Description 1'] || row['DESCRIPTION 1'] || row['Description1'] ||
                        row['Description'] ||
                        row['DESCRIPTION'] ||
                        row['description'] ||
                        '';

                    const description2 =
                        row['Description 2'] || row['DESCRIPTION 2'] || row['Description2'] || '';

                    const givenSupplierName =
                        row['given_Supplier Name'] ||
                        row['given_Supplier Name 1'] ||
                        row['given Supplier Name'] ||
                        row['given Supplier Name 1'] ||
                        row['given_supplier_name'] ||
                        row['Supplier Name'] ||
                        row['Supplier Name 1'] ||
                        row['SupplierName'] ||
                        '';

                    const givenSupplierPartnumber =
                        row['given_Supplier Partnumber'] ||
                        row['given_Supplier Partnumber 1'] ||
                        row['given Supplier Partnumber'] ||
                        row['given Supplier Partnumber 1'] ||
                        row['given_supplier_partnumber'] ||
                        row['Supplier Partnumber'] ||
                        row['Supplier Part Number'] ||
                        row['Supplier Partnumber 1'] ||
                        row['Supplier Part Number 1'] ||
                        '';

                    const supplierPartNumber2 =
                        row['Supplier Partnumber 2'] ||
                        row['Supplier Part Number 2'] ||
                        row['given_Supplier Partnumber 2'] ||
                        row['given Supplier Partnumber 2'] ||
                        '';

                    const supplierPartNumber3 =
                        row['Supplier Partnumber 3'] ||
                        row['Supplier Part Number 3'] ||
                        row['given_Supplier Partnumber 3'] ||
                        row['given Supplier Partnumber 3'] ||
                        '';

                    const supplierPartNumber4 =
                        row['Supplier Partnumber 4'] ||
                        row['Supplier Part Number 4'] ||
                        row['given_Supplier Partnumber 4'] ||
                        row['given Supplier Partnumber 4'] ||
                        '';

                    // Additional supplier names (for supplier priority in URL classification)
                    const givenSupplierName2 =
                        row['given_Supplier Name 2'] ||
                        row['given Supplier Name 2'] ||
                        row['Supplier Name 2'] ||
                        '';

                    const nounModifier =
                        row['Noun/Modifier'] ||
                        row['Noun Modifier'] ||
                        row['NounModifier'] ||
                        row['NOUN/MODIFIER'] ||
                        row['Noun_Modifier'] ||
                        '';

                    return withDescriptions({
                        rowNumber: index + 1,
                        partNumber: String(partnum).trim(),
                        partnum: String(partnum).trim(),
                        manufacturerNumber: String(manufacturerNumber).trim(),
                        manufacturerName: String(manufacturerName).trim(),
                        itemName: String(itemName).trim(),
                        description1: String(description1 || itemDescription1).trim(),
                        description2: String(description2 || itemDescription2).trim(),
                        givenSupplierName: String(givenSupplierName).trim(),
                        givenSupplierPartnumber: String(givenSupplierPartnumber).trim(),
                        givenSupplierName2: String(givenSupplierName2).trim(),
                        supplierPartNumber2: String(supplierPartNumber2).trim(),
                        supplierPartNumber3: String(supplierPartNumber3).trim(),
                        supplierPartNumber4: String(supplierPartNumber4).trim(),
                        nounModifier: String(nounModifier).trim(),
                        status: 'PENDING',
                        rawData: row // Store original row data
                    });
                });

                // Filter out completely empty rows (supports both legacy + automation formats)
                const filteredData = parsedData.filter(item => {
                    return (
                        item.manufacturerNumber ||
                        item.itemName ||
                        item.description1 || item.description2 ||
                        item.givenSupplierName ||
                        item.givenSupplierPartnumber ||
                        item.nounModifier
                    );
                });

                resolve(filteredData);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
    });
};

/**
 * Validate parsed Excel data
 * @param {Array} data - Parsed data
 * @returns {Object} Validation result
 */
export const validateExcelData = (data) => {
    const errors = [];
    const warnings = [];

    if (!data || data.length === 0) {
        errors.push('Excel file is empty');
        return { valid: false, errors, warnings };
    }

    let emptyManufacturerCount = 0;
    let emptyItemDescriptionCount = 0;
    let emptyNameCount = 0;
    let emptyPromptDescriptionCount = 0;
    let emptySupplierNameCount = 0;
    let emptySupplierPartnumberCount = 0;
    let emptyNounModifierCount = 0;
    let emptyPartNumberCount = 0;
    const seenPartNumbers = new Set();
    const duplicatePartNumbers = new Set();

    data.forEach((row, index) => {
        if (!row.partNumber) {
            emptyPartNumberCount++;
        } else {
            const key = row.partNumber.toUpperCase();
            if (seenPartNumbers.has(key)) duplicatePartNumbers.add(row.partNumber);
            seenPartNumbers.add(key);
        }
        if (!row.manufacturerNumber) {
            emptyManufacturerCount++;
        }
        if (!row.description1 && !row.description2) {
            emptyItemDescriptionCount++;
        }
        if (!row.itemName) {
            emptyNameCount++;
        }
        if (!row.description1 && !row.description2) {
            emptyPromptDescriptionCount++;
        }
        if (!row.givenSupplierName) {
            emptySupplierNameCount++;
        }
        if (!row.givenSupplierPartnumber) {
            emptySupplierPartnumberCount++;
        }
        if (!row.nounModifier) {
            emptyNounModifierCount++;
        }
    });

    const hasLegacyFormat = data.some(
        (row) => row.manufacturerNumber || row.itemName || row.description1 || row.description2
    );
    const hasPromptFormat = data.some(
        (row) => row.description1 || row.description2 || row.givenSupplierName || row.givenSupplierPartnumber
    );

    if (!hasLegacyFormat && !hasPromptFormat) {
        errors.push('Input sheet does not contain recognized columns');
        return { valid: false, errors, warnings };
    }

    if (emptyPartNumberCount === data.length) {
        warnings.push('No Part Number column found — add "Part Number" as the first column to identify each record');
    } else if (emptyPartNumberCount > 0) {
        warnings.push(`${emptyPartNumberCount} rows missing Part Number`);
    }
    if (duplicatePartNumbers.size > 0) {
        const sample = [...duplicatePartNumbers].slice(0, 5).join(', ');
        warnings.push(`${duplicatePartNumbers.size} duplicate Part Number(s): ${sample}${duplicatePartNumbers.size > 5 ? '…' : ''}`);
    }

    if (hasLegacyFormat && !hasPromptFormat) {
        if (emptyManufacturerCount > 0) {
            warnings.push(`${emptyManufacturerCount} rows missing Manufacturer Number`);
        }
        if (emptyItemDescriptionCount === data.length) {
            errors.push('All rows are missing Description 1 and Description 2');
        }
        if (emptyNameCount === data.length) {
            errors.push('All rows are missing Item Name');
        }
    }

    if (hasPromptFormat) {
        if (emptyPromptDescriptionCount === data.length) {
            errors.push('All rows are missing Description 1 and Description 2');
        }
        if (emptySupplierNameCount === data.length) {
            errors.push('All rows are missing given_Supplier Name');
        }
        if (emptySupplierPartnumberCount === data.length) {
            errors.push('All rows are missing given_Supplier Partnumber');
        }
        if (emptyNounModifierCount > 0) {
            warnings.push(`${emptyNounModifierCount} rows missing Noun/Modifier`);
        }
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
        stats: {
            totalRows: data.length,
            emptyPartNumberCount,
            duplicatePartNumberCount: duplicatePartNumbers.size,
            emptyManufacturerCount,
            emptyItemDescriptionCount,
            emptyNameCount,
            emptyPromptDescriptionCount,
            emptySupplierNameCount,
            emptySupplierPartnumberCount,
            emptyNounModifierCount
        }
    };
};

/**
 * Parse Noun/Modifier Excel file
 * @param {File} file - Excel file
 * @returns {Promise<Array>} Parsed data
 */
export const parseNounModifierExcel = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                // Get first sheet
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];

                // Convert to JSON
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

                // Extract required columns
                const parsedData = jsonData.map((row, index) => {
                    // Try different possible column names for Partnum
                    const partnum =
                        row['Partnum'] ||
                        row['PartNum'] ||
                        row['Part Num'] ||
                        row['PARTNUM'] ||
                        row['Part_Num'] ||
                        row['PartNumber'] ||
                        row['Part Number'] ||
                        '';

                    // Try different possible column names for Noun/Modifier
                    const nounModifier =
                        row['Noun/Modifier'] ||
                        row['NounModifier'] ||
                        row['Noun Modifier'] ||
                        row['NOUN/MODIFIER'] ||
                        row['Noun_Modifier'] ||
                        row['Noun'] ||
                        row['Modifier'] ||
                        '';

                    return {
                        rowNumber: index + 1,
                        partnum: String(partnum).trim(),
                        nounModifier: String(nounModifier).trim(),
                        rawData: row // Store original row data
                    };
                });

                // Filter out completely empty rows (where both key fields are empty)
                const filteredData = parsedData.filter(item => {
                    return item.partnum || item.nounModifier;
                });

                resolve(filteredData);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
    });
};

/**
 * Validate parsed Noun/Modifier data
 * @param {Array} data - Parsed data
 * @returns {Object} Validation result
 */
export const validateNounModifierData = (data) => {
    const errors = [];
    const warnings = [];

    if (!data || data.length === 0) {
        errors.push('Excel file is empty');
        return { valid: false, errors, warnings };
    }

    let emptyPartnumCount = 0;
    let emptyNounModifierCount = 0;

    data.forEach((row, index) => {
        if (!row.partnum) {
            emptyPartnumCount++;
        }
        if (!row.nounModifier) {
            emptyNounModifierCount++;
        }
    });

    if (emptyPartnumCount > 0) {
        warnings.push(`${emptyPartnumCount} rows missing Partnum`);
    }

    if (emptyNounModifierCount > 0) {
        warnings.push(`${emptyNounModifierCount} rows missing Noun/Modifier`);
    }

    if (emptyPartnumCount === data.length) {
        errors.push('All rows are missing Partnum - please check column name');
    }

    if (emptyNounModifierCount === data.length) {
        errors.push('All rows are missing Noun/Modifier - please check column name');
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
        stats: {
            totalRows: data.length,
            emptyPartnumCount,
            emptyNounModifierCount
        }
    };
};

/**
 * Parse INC Excel file
 * @param {File} file - Excel file
 * @returns {Promise<Array>} Parsed data with ESCN, SEQ, and Property columns
 */
export const parseIncExcel = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                // Get first sheet
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];

                // Convert to JSON
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

                // Extract required columns
                const parsedData = jsonData.map((row, index) => {
                    // Try different possible column names for ESCN
                    const escn =
                        row['ESCN'] ||
                        row['Escn'] ||
                        row['escn'] ||
                        row['ESCN_NUM'] ||
                        row['EscnNum'] ||
                        '';

                    // Try different possible column names for SEQ
                    const seq =
                        row['SEQ'] ||
                        row['Seq'] ||
                        row['seq'] ||
                        row['SEQUENCE'] ||
                        row['Sequence'] ||
                        '';

                    // Try different possible column names for Property
                    const property =
                        row['Property'] ||
                        row['PROPERTY'] ||
                        row['property'] ||
                        row['Properties'] ||
                        row['PROPERTIES'] ||
                        '';

                    return {
                        rowNumber: index + 1,
                        escn: String(escn).trim(),
                        seq: String(seq).trim(),
                        property: String(property).trim(),
                    };
                });

                // Filter out completely empty rows
                const filteredData = parsedData.filter(item => {
                    return item.escn || item.seq || item.property;
                });

                resolve(filteredData);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
    });
};

/**
 * Validate parsed INC data
 * @param {Array} data - Parsed data
 * @returns {Object} Validation result
 */
export const validateIncData = (data) => {
    const errors = [];
    const warnings = [];

    if (!data || data.length === 0) {
        errors.push('INC Excel file is empty');
        return { valid: false, errors, warnings };
    }

    let emptyEscnCount = 0;
    let emptySeqCount = 0;
    let emptyPropertyCount = 0;

    data.forEach((row, index) => {
        if (!row.escn) {
            emptyEscnCount++;
        }
        if (!row.seq) {
            emptySeqCount++;
        }
        if (!row.property) {
            emptyPropertyCount++;
        }
    });

    if (emptyEscnCount > 0) {
        warnings.push(`${emptyEscnCount} rows missing ESCN`);
    }

    if (emptySeqCount > 0) {
        warnings.push(`${emptySeqCount} rows missing SEQ`);
    }

    if (emptyPropertyCount > 0) {
        warnings.push(`${emptyPropertyCount} rows missing Property`);
    }

    if (emptyEscnCount === data.length) {
        errors.push('All rows are missing ESCN - please check column name');
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
        stats: {
            totalRows: data.length,
            emptyEscnCount,
            emptySeqCount,
            emptyPropertyCount
        }
    };
};
